import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';

import { ALL_ITEMS } from '@/modules/analyzer/data/rubric';
import { GOALS } from '@/modules/analyzer/data/goals';
import { uid } from '@/modules/analyzer/tools/format';
import type { Audit, AuditGoal, CriterionState } from '@/modules/analyzer/types';
import * as api from '@/modules/analyzer/api';
import type { ServerAudit } from '@/modules/analyzer/api';
import { useAuthStore } from '@/stores/auth';

/**
 * A row as held in the store. The server's list query omits the heavy JSON
 * `document`, so a freshly-listed audit carries only the promoted columns plus
 * the denormalized counters (figureCount/videoCount/criticalCount) — none of
 * the nested document fields (stats/figures/videos/criteria/…). Selecting a row
 * loads its document via `getAudit` and MERGES those fields into this same
 * object (identity preserved), at which point it satisfies the full client
 * `Audit`. So document-derived fields are optional at the type level and code
 * that reads them off a list row must guard (see SavedAuditList.chipMeta / R5).
 * `current` is only ever set to a row after its document is loaded, so it is
 * exposed as a full `Audit`.
 */
export type StoreAudit = Partial<Audit> & {
  id: string;
  figureCount?: number;
  videoCount?: number;
  criticalCount?: number;
};

/** Server-only list counters — never round-tripped back inside `document`. */
const COUNT_KEYS = new Set(['figureCount', 'videoCount', 'criticalCount']);

/** Drop UI-only ("_"-prefixed) fields AND the server list counters on serialize. */
const persistReplacer = (key: string, value: unknown): unknown =>
  key.startsWith('_') || COUNT_KEYS.has(key) ? undefined : value;

/**
 * Recursively sort object keys so serializations are stable: Postgres jsonb
 * does not preserve key order, so a server snapshot and the identical client
 * object would otherwise stringify differently and defeat the echo check.
 */
const sortKeysDeep = (v: unknown): unknown => {
  if (Array.isArray(v)) return v.map(sortKeysDeep);
  if (v && typeof v === 'object') {
    const src = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(src).sort()) out[k] = sortKeysDeep(src[k]);
    return out;
  }
  return v;
};

const serializeDocument = (a: StoreAudit): string => JSON.stringify(sortKeysDeep(a), persistReplacer);

/** Whether a row has its `document` loaded (list rows carry only promoted columns). */
const isLoaded = (a: StoreAudit): boolean => a.criteria != null;

/* ---------- Legacy localStorage migration (Q3, one-time) ---------- */

const LEGACY_INDEX = 'ca:index';
const legacyKey = (id: string): string => 'ca:' + id;

const DEFAULT_GOAL: AuditGoal = 'subscriber_campaign';

/**
 * Normalise an audit loaded from the server (or legacy storage) so it carries
 * every field the current rubric expects — backfills criteria added since it
 * was saved (e.g. category H) and repairs missing arrays. Mirrors the
 * prototype's migrateAudit().
 */
function migrateAudit(a: Audit): Audit {
  a.criteria = a.criteria || {};
  if (!a.goal) a.goal = DEFAULT_GOAL;
  const gmap = (GOALS[a.goal] ?? GOALS[DEFAULT_GOAL]).map;
  a.include = a.include || {};
  ALL_ITEMS.forEach((i) => {
    if (!a.criteria[i.id]) a.criteria[i.id] = { status: 'na', finding: '', action: '', figs: [] };
    else if (!Array.isArray(a.criteria[i.id]!.figs)) a.criteria[i.id]!.figs = [];
    if (typeof a.include[i.id] === 'undefined') a.include[i.id] = gmap[i.id] !== 'optional';
  });
  if (!Array.isArray(a.objectives)) a.objectives = [];
  if (typeof a.objectivesConfirmed === 'undefined') a.objectivesConfirmed = false;
  if (!Array.isArray(a.figures)) a.figures = [];
  if (!Array.isArray(a.videos)) a.videos = [];
  return a;
}

function blankAudit(): Audit {
  const criteria: Record<string, CriterionState> = {};
  const include: Record<string, boolean> = {};
  ALL_ITEMS.forEach((i) => {
    criteria[i.id] = { status: 'na', finding: '', action: '', figs: [] };
    include[i.id] = GOALS[DEFAULT_GOAL].map[i.id] !== 'optional';
  });

  const videos: Audit['videos'] = [];
  for (let k = 0; k < 5; k++) {
    videos.push({ id: uid(), kind: 'viewed', format: 'long', title: '', description: '', views: '', date: '', fig: '' });
  }
  for (let k = 0; k < 5; k++) {
    videos.push({ id: uid(), kind: 'recent', format: 'long', title: '', description: '', views: '', date: '', fig: '' });
  }

  return {
    id: uid(),
    name: 'Untitled channel',
    handle: '',
    tier: 'presentation',
    date: new Date().toISOString().slice(0, 10),
    stats: { subs: '', videos: '', views: '' },
    cadence: '',
    trailer: 'unknown',
    trailerData: null,
    figures: [],
    videos,
    criteria,
    goal: DEFAULT_GOAL,
    include,
    objectives: [],
    objectivesConfirmed: false,
    summary: '',
    verdict: 'auto',
  };
}

/**
 * Unwrap a loaded server row into the client shape: the `document` fields (the
 * app's components consume `audit.figures`, `audit.criteria`, …) with the row
 * id and the list counters kept alongside for card rendering.
 */
function toClientAudit(row: ServerAudit): StoreAudit {
  const doc = migrateAudit({ ...(row.document as Audit), id: row.id });
  return {
    ...doc,
    figureCount: row.figureCount,
    videoCount: row.videoCount,
    criticalCount: row.criticalCount,
  };
}

/** A list row: promoted columns + counters only (document merged in on select). */
function toListRow(row: ServerAudit): StoreAudit {
  return {
    id: row.id,
    name: row.name,
    handle: row.handle,
    tier: row.tier,
    goal: row.goal,
    verdict: row.verdict,
    date: row.date,
    channelUrl: row.channelUrl ?? undefined,
    figureCount: row.figureCount,
    videoCount: row.videoCount,
    criticalCount: row.criticalCount,
  };
}

/** How a server response is folded into the in-store audit (spec R3 / A5). */
export type MergeTarget =
  | { kind: 'document' }
  | { kind: 'keys'; keys: (keyof Audit)[] }
  | { kind: 'criterion'; criterionId: string };

/** UI-only ("_"-prefixed) keys of an object, preserved across a whole-doc merge. */
function underscoreFields(o: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(o)) if (k.startsWith('_')) out[k] = o[k];
  return out;
}

export const useAuditsStore = defineStore('audits', () => {
  const audits = ref<StoreAudit[]>([]);
  const currentId = ref<string | null>(null);

  // Only ever points at a row whose document has been loaded (see `select`).
  const current = computed<Audit | null>(() => {
    const a = audits.value.find((x) => x.id === currentId.value);
    return a ? (a as Audit) : null;
  });

  // Echo suppression: the last document we know the server holds for each id,
  // serialized. The debounced watcher skips an audit whose serialization still
  // equals this, so applying a server response never triggers a redundant
  // `updateAudit` write (A5 double-write guard).
  const lastPersisted: Record<string, string> = {};

  function markPersisted(a: StoreAudit): void {
    lastPersisted[a.id] = serializeDocument(a);
  }

  /**
   * Overwrite `target`'s document fields with the server `doc` in place,
   * preserving object identity and any UI-only state (expanded rows, active
   * report view) the server document — persisted without "_"-fields — cannot
   * carry.
   */
  function mergeWholeDocument(target: StoreAudit, doc: Audit): void {
    const rootUi = underscoreFields(target as Record<string, unknown>);
    const videoUi = new Map<string, Record<string, unknown>>();
    (target.videos ?? []).forEach((v) => videoUi.set(v.id, underscoreFields(v as unknown as Record<string, unknown>)));

    Object.assign(target, migrateAudit({ ...doc }));

    Object.assign(target, rootUi);
    (target.videos ?? []).forEach((v) => {
      const ui = videoUi.get(v.id);
      if (ui) Object.assign(v, ui);
    });
  }

  /**
   * Fold a server operation's response back into the store, MERGING by target
   * so the parallel CampaignAnalysis trio never clobber one another client-side
   * (R3/A5). The audit object identity in `audits.value` is preserved (mutated
   * in place) so `props.audit` holders keep working. `lastPersisted` is
   * baselined from the SERVER snapshot so the watcher fires no echo write when
   * the client is clean, yet still writes any field edited while the op was in
   * flight.
   */
  function applyServerAudit(row: ServerAudit, target: MergeTarget = { kind: 'document' }): void {
    const a = audits.value.find((x) => x.id === row.id);
    const doc = row.document;
    if (!a || !doc) return;

    if (target.kind === 'document') {
      // Whole-document ops (analyze/import/fetch) — single-flight, replace all.
      mergeWholeDocument(a, doc);
    } else if (target.kind === 'criterion') {
      // draftCriterion — touch only the one criterion (parallel-safe).
      const cr = doc.criteria?.[target.criterionId];
      a.criteria = a.criteria ?? {};
      if (cr) a.criteria[target.criterionId] = cr;
    } else {
      // Single-key AI writes (summary / health / brandSafety / creativeReadiness).
      for (const key of target.keys) Object.assign(a, { [key]: doc[key] });
    }

    a.figureCount = row.figureCount;
    a.videoCount = row.videoCount;
    a.criticalCount = row.criticalCount;
    // Baseline the echo check on the SERVER snapshot, not the merged client
    // object: a field the user edited while this op was in flight must still
    // read as dirty so the next debounce tick writes it. That full-doc write
    // already carries the merged AI keys, so it cannot clobber the server's
    // jsonb_set results (client-side A5).
    lastPersisted[a.id] = serializeDocument(toClientAudit(row));
  }

  /**
   * Bootstrap: load the signed-in user's audits. A no-op without a live session
   * (the app shell stays usable); re-run after login. Any auth/transport failure
   * leaves the store empty rather than crashing the shell.
   */
  async function init(): Promise<void> {
    const auth = useAuthStore();
    if (!auth.isAuthenticated) return;
    try {
      const rows = await api.listAudits();
      if (rows.length) {
        const first = await api.getAudit(rows[0]!.id);
        const list: StoreAudit[] = rows.map((r, i) => (i === 0 ? toClientAudit(first) : toListRow(r)));
        list.forEach(markPersisted);
        audits.value = list;
        currentId.value = list[0]!.id;
      } else {
        audits.value = [];
        currentId.value = null;
        await create();
      }
    } catch {
      audits.value = [];
      currentId.value = null;
    }
  }

  async function create(): Promise<StoreAudit> {
    const created = toClientAudit(await api.createAudit(blankAudit()));
    markPersisted(created);
    audits.value.unshift(created);
    currentId.value = created.id;
    return created;
  }

  /** Select a row, lazily loading + merging its document first (identity kept). */
  async function select(id: string): Promise<void> {
    const existing = audits.value.find((a) => a.id === id);
    if (existing && !isLoaded(existing)) {
      const merged = toClientAudit(await api.getAudit(id));
      Object.assign(existing, merged);
      markPersisted(existing);
    }
    currentId.value = id;
  }

  async function remove(id: string): Promise<void> {
    await api.deleteAudit(id);
    audits.value = audits.value.filter((a) => a.id !== id);
    delete lastPersisted[id];
    if (currentId.value === id) {
      const next = audits.value[0];
      if (next) await select(next.id);
      else currentId.value = null;
    }
  }

  async function addDemo(a: Audit): Promise<StoreAudit> {
    const created = toClientAudit(await api.createAudit(a));
    markPersisted(created);
    audits.value.unshift(created);
    currentId.value = created.id;
    return created;
  }

  /**
   * One-time migration (Q3): pull audits saved by the old localStorage build
   * into the account, then clear the legacy keys. Best-effort, no dedup.
   * Returns the number of audits imported.
   */
  async function importLocalAudits(): Promise<number> {
    let ids: string[] = [];
    try {
      const raw = localStorage.getItem(LEGACY_INDEX);
      ids = raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      ids = [];
    }

    let imported = 0;
    for (const id of ids) {
      let doc: Audit | null = null;
      try {
        const raw = localStorage.getItem(legacyKey(id));
        if (raw) doc = migrateAudit(JSON.parse(raw) as Audit);
      } catch {
        doc = null;
      }
      if (!doc) continue;
      try {
        const created = toClientAudit(await api.createAudit(doc));
        markPersisted(created);
        audits.value.unshift(created);
        imported++;
      } catch {
        /* skip a single bad audit */
      }
    }

    try {
      for (const id of ids) localStorage.removeItem(legacyKey(id));
      localStorage.removeItem(LEGACY_INDEX);
    } catch {
      /* ignore */
    }

    if (imported && !currentId.value) currentId.value = audits.value[0]?.id ?? null;
    return imported;
  }

  /**
   * Debounced autosave: whole-document write for any loaded audit whose
   * serialized document has drifted from the server's copy. Skips list rows
   * (no document) and server-echo ticks (equal serialization) so it never
   * double-writes an op the composables already applied.
   */
  function persist(): void {
    for (const a of audits.value) {
      if (!isLoaded(a)) continue;
      const serialized = serializeDocument(a);
      if (serialized === lastPersisted[a.id]) continue;
      lastPersisted[a.id] = serialized;
      const document = JSON.parse(serialized) as Audit;
      void api.updateAudit(a.id, document).catch(() => {
        // Best-effort — drop the marker so the next tick retries.
        delete lastPersisted[a.id];
      });
    }
  }

  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  watch(
    audits,
    () => {
      clearTimeout(saveTimer);
      saveTimer = setTimeout(persist, 400);
    },
    { deep: true },
  );

  return {
    audits,
    currentId,
    current,
    init,
    create,
    select,
    remove,
    addDemo,
    persist,
    blankAudit,
    applyServerAudit,
    importLocalAudits,
  };
});
